import React from "react";
import { useQuery, useSubscription } from "@apollo/client";
import { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import {
  QUERY_LATEST_RESS_ENERGY_RUN,
  SUBSCRIBE_RESS_ENERGY_RUN,
} from "../../utils/graphql/queries/ressQueries";
import { RessEnergySelection } from "../../types/ress";
import styles from "./style.module.scss";

type RessEnergyRun = RessEnergySelection & {
  completedAt: number;
  dataFile: string;
};

function chartOptions(
  title: string,
  beginAtZero = false,
): ChartOptions<"line"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { labels: { color: "#ffffff" } },
      title: { display: true, text: title, color: "#ffffff" },
    },
    scales: {
      x: {
        type: "linear",
        title: { display: true, text: "Lap within run", color: "#ffffff" },
        ticks: { color: "#ffffff", precision: 0 },
        grid: { color: "#505050" },
      },
      y: {
        beginAtZero,
        title: { display: true, text: "Energy (kWh)", color: "#ffffff" },
        ticks: { color: "#ffffff" },
        grid: { color: "#505050" },
      },
    },
  };
}

const energyOptions = chartOptions("Target vs selected lap energy");
const deltaOptions = chartOptions("Energy delta: selected minus target", true);

function series(label: string, values: number[], colour: string) {
  return {
    label,
    data: values.map((energy, index) => ({ x: index + 1, y: energy })),
    borderColor: colour,
    backgroundColor: colour,
    borderWidth: 2,
    pointRadius: 2,
    tension: 0,
  };
}

export default function EnergySelectionGraphs() {
  const { data, error: queryError } = useQuery<{
    getLatestRessEnergyRun: RessEnergyRun | null;
  }>(QUERY_LATEST_RESS_ENERGY_RUN, {
    fetchPolicy: "network-only",
    context: { skipRetry: true },
  });
  const { data: updates, error: subscriptionError } = useSubscription<{
    newRessEnergyRun: RessEnergyRun;
  }>(SUBSCRIBE_RESS_ENERGY_RUN);
  const initial = data?.getLatestRessEnergyRun;
  const update = updates?.newRessEnergyRun;
  const run =
    update && (!initial || update.completedAt >= initial.completedAt)
      ? update
      : initial;
  const error = queryError ?? subscriptionError;

  return (
    <EnergySelectionResults
      selection={run}
      title="Latest run"
      description={
        run
          ? `${run.dataFile} · ${new Date(run.completedAt).toLocaleString()}.`
          : undefined
      }
      error={error?.message}
    />
  );
}

type EnergySelectionResultsProps = {
  selection: RessEnergySelection | null | undefined;
  title: string;
  description?: string;
  showDelta?: boolean;
  error?: string;
  emptyMessage?: string;
};

export function EnergySelectionResults({
  selection,
  title,
  description,
  showDelta = true,
  error,
  emptyMessage = "Waiting for a completed RESS run.",
}: EnergySelectionResultsProps) {
  const targets = selection?.targetEnergykWh ?? [];
  const selected = selection?.selectedEnergykWh ?? [];
  const deltas = selected.map((energy, index) => energy - targets[index]);
  let accumulated = 0;
  const cumulativeDeltas = deltas.map((delta) => (accumulated += delta));
  const targetTotal = targets.reduce((total, energy) => total + energy, 0);
  const selectedTotal = selected.reduce((total, energy) => total + energy, 0);

  return (
    <section className={styles.energyPanel} aria-labelledby="ress-energy-title">
      <div className={styles.energyHeader}>
        <h2 id="ress-energy-title">{title}</h2>
      </div>
      {error && (
        <p className={styles.energyError} role="alert">
          Energy results unavailable: {error}
        </p>
      )}
      {selection ? (
        <>
          {description && <p className={styles.energyHint}>{description}</p>}
          <div
            className={`${styles.energyCharts} ${
              showDelta ? "" : styles.singleEnergyChart
            }`}
          >
            <div className={styles.energyChartCanvas}>
              <Line
                data={{
                  datasets: [
                    series("Required target", targets, "#a18b6b"),
                    series("Selected simulated energy", selected, "#6fa8dc"),
                  ],
                }}
                options={energyOptions}
                role="img"
                aria-label="Required and selected energy for each lap"
              />
            </div>
            {showDelta && (
              <div className={styles.energyChartCanvas}>
                <Line
                  data={{
                    datasets: [
                      series("Lap delta", deltas, "#6fa8dc"),
                      series("Cumulative delta", cumulativeDeltas, "#e7a24b"),
                    ],
                  }}
                  options={deltaOptions}
                  role="img"
                  aria-label="Per-lap and cumulative energy delta"
                />
              </div>
            )}
          </div>
          <p className={styles.energyHint} aria-live="polite">
            Target total: {targetTotal.toFixed(4)} kWh · Selected total:{" "}
            {selectedTotal.toFixed(4)} kWh · Final delta:{" "}
            {(selectedTotal - targetTotal).toFixed(4)} kWh.
          </p>
        </>
      ) : (
        <p className={styles.energyHint}>{emptyMessage}</p>
      )}
    </section>
  );
}
